<template>
  <div class="wai">
    <!-- 表单卡片 -->
    <div class="card">
      <!-- 卡片头部标题步骤 -->
      <div class="card-head">
        <div class="card-title">核验业务申请</div>
        <div class="card-desc">请填写申请人信息并上传授权书，我们将在 <b>1 个工作日内</b> 完成审核。</div>
        <div class="steps">
          <div class="step active">
            <span class="step-num">1</span>
            <span>填写信息</span>
          </div>
          <div class="step-divider"></div>
          <div class="step">
            <span class="step-num">2</span>
            <span>上传材料</span>
          </div>
          <div class="step-divider"></div>
          <div class="step">
            <span class="step-num">3</span>
            <span>完成授权</span>
          </div>
        </div>
      </div>

      <!-- 表单主体 -->
      <div class="form">
        <!-- 第一块：两个输入框 姓名+证件号码 -->
        <div class="section">
          <div class="section-title">申请人信息 <span class="section-sub">用于身份核验</span></div>
          <div class="grid">
            <div class="field">
              <label class="label">姓名 <span class="req">*</span></label>
              <div class="control">
                <input v-model="form.name" type="text" placeholder="请输入真实姓名" />
              </div>
              <div class="field-error" v-if="errorMsg.name">{{ errorMsg.name }}</div>
            </div>
            <div class="field">
              <label class="label">证件号码 <span class="req">*</span></label>
              <div class="control">
                <input v-model="form.cardid" type="text" placeholder="请输入授权人证件号码（18位）" maxlength="18" />
              </div>
              <div class="field-error" v-if="errorMsg.cardid">{{ errorMsg.cardid }}</div>
            </div>
          </div>
        </div>

        <!-- 第二块：文件上传区域 -->
        <div class="section">
          <div class="section-title">授权材料 <span class="section-sub">仅用于本次申请的合规核验（含CA数字证书PDF）</span></div>
          <label class="upload" @click="triggerUpload">
            <div class="upload-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M12 16V6M12 6l-4 4M12 6l4 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </div>
            <div class="upload-text">
              <div class="t1"><span class="accent">点击上传</span>或将文件拖拽到此处</div>
              <div class="t2">仅支持 PDF 格式，单个文件不超过 10MB，需包含CA数字证书</div>
            </div>
            <button type="button" class="upload-btn" @click.stop="triggerUpload">选择文件</button>
            <input ref="fileInput" type="file" accept=".pdf" style="display:none" @change="handleFileChange" />
          </label>
          <div style="margin-top:10px;color:#6b7280;font-size:13px" v-if="fileObj">
            已选择文件：{{ fileObj.name }}
          </div>
        </div>

        <!-- 第三块：阅读协议勾选【已更新合规文案】 -->
        <div class="section">
          <div class="section-title">服务协议 <span class="section-sub">请仔细阅读后勾选</span></div>
          <label class="agreement">
            <input v-model="form.agree" type="checkbox" class="check" />
            <div class="agreement-text">
              我已完整阅读并自愿同意
              <a href="javascript:;" @click="openDialog('service')">《用户服务协议》</a>
              与
              <a href="javascript:;" @click="openDialog('privacy')">《数据使用与隐私政策》</a>，
              确认所提交信息真实有效；本人承诺<strong>已事先取得被核验人书面授权或同等法律效力电子授权</strong>，授权仅限本次贷前风险核验使用，并授权蓝小小对所提交材料开展合规核验。
            </div>
          </label>
          <div class="field-error" v-if="errorMsg.agree" style="margin-top:8px">{{ errorMsg.agree }}</div>
        </div>

        <!-- 安全提示条 -->
        <div class="trust">
          <span class="trust-dot"></span>
          全程加密传输 · 公安部信息安全等级保护三级 · 7×24 人工审核
        </div>

        <!-- 操作按钮 -->
        <div class="actions">
          <button class="btn btn-primary" @click="submitForm" :disabled="submitLoading">
            提交申请
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
          <button class="btn btn-ghost" @click="resetForm">重置</button>
          <div class="spacer"></div>
          <div class="action-tip">提交即代表您已阅读并同意相关协议</div>
        </div>
      </div>
    </div>

    <!-- 右侧调用额度面板 -->
    <div class="right-panel">
      <div class="panel-title">接口调用额度</div>
      <div class="row">
        <span class="label">当日调用：</span>
        <span class="num">0 / 0</span>
      </div>
      <div class="row">
        <span class="label">当月调用：</span>
        <span class="num">0 / 0</span>
      </div>
    </div>

    <!-- ========== 协议弹窗层 ========== -->
    <!-- 遮罩 -->
    <div class="dialog-mask" v-if="showDialog" @click="closeDialog"></div>
    <!-- 弹窗容器 -->
    <div class="dialog-wrap" v-if="showDialog">
      <div class="dialog-box" @click.stop>
        <div class="dialog-header">
          <h3>{{ dialogTitle }}</h3>
          <span class="close-btn" @click="closeDialog">✕</span>
        </div>
        <div class="dialog-content">
          <!-- 用户服务协议 -->
          <div v-if="dialogType === 'service'">
            <p><strong>生效日期：2026年07月28日</strong></p>
            <p>欢迎使用蓝小小平台服务！在您勾选同意并提交核验申请后，即代表您已完整阅读、充分理解并自愿遵守本《用户服务协议》全部条款，双方服务合作关系即刻生效。</p>
            <p><strong>一、服务内容</strong></p>
            <p>本平台为用户提供不动产材料合规核验、资质信息校验服务，所有服务场景<strong>仅限合法贷前风险核验使用</strong>，严禁用于催收、营销、征信替代、数据交易、信息倒卖等违规违法场景。未经平台书面许可，用户不得复用、转发、留存核验数据与核验结果。</p>
            <p><strong>二、用户合规核心承诺</strong></p>
            <p>1. 本人郑重承诺，在提交本次核验申请前，<strong>已合法合规取得被核验人本人的书面授权或具备同等法律效力的电子授权文件</strong>，被核验人已明确知晓本次信息核验的用途、范围、方式，自愿同意本次核验操作，不存在无权核验、授权过期、授权瑕疵、冒用他人信息等情形。</p>
            <p>2. 本次提交的姓名、身份证号码、带CA数字证书的PDF授权文件等全部资料，均真实、合法、有效，无伪造、篡改、变造等违规行为。</p>
            <p>3. 本次核验获取的所有信息，<strong>仅用于本次单次贷前风险核验</strong>，不做二次使用、永久存储、对外流转、跨场景复用。</p>
            <p><strong>三、责任界定</strong></p>
            <p>因用户未取得有效授权、提交虚假材料、超范围使用数据、违规流转信息等行为，引发的民事纠纷、行政处罚、刑事责任，均由用户自行独立承担；若因此造成平台商誉损失、经济损失、法务成本，用户需全额承担赔偿责任，平台不承担任何连带责任。</p>
            <p><strong>四、其他说明</strong></p>
            <p>平台有权对用户提交的材料进行合规审核，对违规、虚假、授权不全的申请予以驳回；平台保留对服务规则的优化、调整权利，调整后将通过页面公示生效。</p>
          </div>
          <!-- 数据使用与隐私政策 -->
          <div v-if="dialogType === 'privacy'">
            <p><strong>生效日期：2026年07月28日</strong></p>
            <p>为严格遵守《中华人民共和国个人信息保护法》《网络安全法》等法律法规，规范个人信息收集、使用、存储、保护行为，保障被核验人个人信息合法权益，特制定本隐私政策。用户提交核验申请即视为认可本政策全部内容。</p>
            <p><strong>一、信息收集范围</strong></p>
            <p>平台仅收集本次贷前核验所需的必要信息，包含申请人提交的姓名、身份证号码、带CA数字证书的授权PDF文件及核验所需的不动产合规材料，不收集无关个人敏感信息。</p>
            <p><strong>二、信息使用目的与限制</strong></p>
            <p>1. 所有收集的用户及被核验人信息，<strong>唯一用途为本次单次贷前风险合规核验</strong>，无其他任何使用场景。</p>
            <p>2. 严格遵循“最小必要、一事一用”原则，绝不超范围收集、超权限使用、超期限留存个人信息。</p>
            <p>3. 未经被核验人及平台双重许可，不得将核验信息泄露、出售、提供给任何第三方，不得用于本次核验以外的任何商业或非法用途。</p>
            <p><strong>三、信息存储与安全保护</strong></p>
            <p>1. 平台全程采用加密传输、加密存储技术，依托等级保护三级安全体系，保障用户及被核验人信息安全，防范信息泄露、丢失、篡改、滥用风险。</p>
            <p>2. 本次核验完成后，平台将按照合规要求清理、脱敏相关核验数据，不长期留存用户敏感信息。</p>
            <p><strong>四、用户与被核验人权利</strong></p>
            <p>被核验人享有个人信息查询、更正、删除、撤回授权的合法权利，若需维权可通过平台官方渠道提交申请，平台将在合规范围内及时处理。</p>
            <p><strong>五、违规责任</strong></p>
            <p>用户若违规收集、使用、泄露、流转个人信息，违反本隐私政策及国家法律法规的，需自行承担全部法律责任，平台有权终止服务、驳回申请，并追究相关损失赔偿责任。</p>
          </div>
        </div>
        <div class="dialog-footer">
          <button class="dialog-btn confirm" @click="closeDialog">我已阅读</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ElMessage } from 'element-plus'
import { ref, reactive } from 'vue'
import { createBdcOrder } from '@/api/api'

// 文件输入dom
const fileInput = ref(null)
// 提交加载状态
const submitLoading = ref(false)
// 文件对象
const fileObj = ref(null)
// PDF base64文本
const pdfBase64 = ref('')
// 防抖定时器
let submitDebounceTimer = null

// 弹窗控制
const showDialog = ref(false)
const dialogType = ref('service')
const dialogTitle = ref('')
const openDialog = (type) => {
  dialogType.value = type
  if (type === 'service') {
    dialogTitle.value = '用户服务协议'
  } else {
    dialogTitle.value = '数据使用与隐私政策'
  }
  showDialog.value = true
}
const closeDialog = () => {
  showDialog.value = false
}

// =====表单数据（对齐后端字段名）=====
const form = reactive({
  goodid: '1',    // 商品ID，可根据路由动态赋值
  name: '',
  cardid: '',
  agree: false
})

// 表单错误提示
const errorMsg = reactive({
  name: '',
  cardid: '',
  agree: ''
})

// 唤起文件选择框
const triggerUpload = () => {
  fileInput.value.click()
}

// 文件选择回调：读取PDF转为base64
const handleFileChange = (e) => {
  const file = e.target.files[0]
  if (!file) return

  // 限制pdf
  if (!file.name.endsWith('.pdf')) {
    ElMessage.error('仅支持上传PDF文件')
    return
  }
  // 限制10MB
  if (file.size > 10 * 1024 * 1024) {
    ElMessage.error('文件不能超过10MB')
    return
  }

  fileObj.value = file
  // 文件转Base64
  const reader = new FileReader()
  reader.readAsDataURL(file)
  reader.onload = function () {
    // 去除data:application/pdf;base64,前缀，只保留纯base64字符串
    const base64Raw = reader.result
    pdfBase64.value = base64Raw.split(',')[1]
  }
}

// 身份证正则 18位
const idCardReg = /^[1-9]\d{5}(19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])\d{3}[\dXx]$/

/**
 * 根据18位身份证计算周岁
 * @param {string} idCard
 * @returns {number} age
 */
const getAgeFromIdCard = (idCard) => {
  const birthStr = idCard.substring(6, 14)
  const birthYear = Number(birthStr.slice(0, 4))
  const birthMonth = Number(birthStr.slice(4, 6))
  const birthDay = Number(birthStr.slice(6, 8))

  const now = new Date()
  const currentYear = now.getFullYear()
  const currentMonth = now.getMonth() + 1
  const currentDay = now.getDate()

  let age = currentYear - birthYear
  if (currentMonth < birthMonth || (currentMonth === birthMonth && currentDay < birthDay)) {
    age -= 1
  }
  return age
}

// 表单校验
const validateForm = () => {
  let flag = true
  // 清空错误
  errorMsg.name = ''
  errorMsg.cardid = ''
  errorMsg.agree = ''

  if (!form.name.trim()) {
    errorMsg.name = '请输入真实姓名'
    flag = false
  }
  if (!form.cardid.trim()) {
    errorMsg.cardid = '请输入证件号码'
    flag = false
  } else if (!idCardReg.test(form.cardid)) {
    errorMsg.cardid = '请输入合法18位身份证号码'
    flag = false
  } else {
    // 校验年龄区间 18‑60周岁
    const age = getAgeFromIdCard(form.cardid)
    if (age < 18 || age > 60) {
      errorMsg.cardid = `年龄校验失败，仅支持18‑60周岁，当前解析年龄：${age}岁`
      flag = false
    }
  }
  if (!form.agree) {
    errorMsg.agree = '请阅读并勾选同意协议'
    flag = false
  }
  if (!fileObj.value || !pdfBase64.value) {
    ElMessage.warning('请上传授权材料PDF文件')
    flag = false
  }
  return flag
}

// 提交表单（增加防抖，300ms间隔）
const submitForm = () => {
  if(submitDebounceTimer) return
  submitDebounceTimer = setTimeout(async ()=>{
    if (!validateForm()) {
      submitDebounceTimer = null
      return
    }
    submitLoading.value = true

    // 组装后端要求JSON参数
    const submitParams = {
      goodid: form.goodid,
      name: form.name,
      cardid: form.cardid,
      file: "data:application/pdf;base64," + pdfBase64.value
    }

    try {
      // 请求不动产下单接口
      const res = await createBdcOrder(submitParams)
      if (res.code === 200) {
        ElMessage.success('提交成功，等待审核')
        resetForm()
        // 可在这里添加路由跳转逻辑
        // router.push('/xxx')
      } else {
        ElMessage.error(res.message || '提交失败，请稍后重试')
      }
    } catch (err) {
      console.error('申请接口异常：', err)
      ElMessage.error('网络异常，提交失败')
    } finally {
      submitLoading.value = false
      submitDebounceTimer = null
    }
  },300)
}

// 重置表单
const resetForm = () => {
  form.name = ''
  form.cardid = ''
  form.agree = false
  fileObj.value = null
  pdfBase64.value = ''
  // 清空错误
  errorMsg.name = ''
  errorMsg.cardid = ''
  errorMsg.agree = ''
  // 清空文件输入
  if (fileInput.value) fileInput.value.value = ''
  // 清除防抖定时器
  if(submitDebounceTimer){
    clearTimeout(submitDebounceTimer)
    submitDebounceTimer = null
  }
}

// 【在这里请求后端获取额度数据】
// const getQuota = async ()=>{
//   const res = await api.getQuota()
//   Object.assign(quota, res.data)
// }
// getQuota()
</script>

<style lang="scss" scoped>
.wai {
  width: 100%;
  min-height: 100vh;
  padding: 12px 24px;
  font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "Microsoft YaHei", "Helvetica Neue", Arial, sans-serif;
  color: #1f2937;
  background:
    radial-gradient(1200px 600px at 10% -10%, #e8efff 0%, transparent 60%),
    radial-gradient(900px 500px at 110% 110%, #f0eaff 0%, transparent 55%),
    linear-gradient(180deg, #f7f9ff 0%, #eef2fb 100%);
  -webkit-font-smoothing: antialiased;
  line-height: 1.55;

  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  align-items: flex-start;

  /* 卡片容器 */
  .card {
    flex: 1;
    min-width: 320px;
    max-width: 960px;
    background: #ffffff;
    border-radius: 16px;
    box-shadow:
      0 1px 2px rgba(16, 24, 40, 0.04),
      0 12px 32px -12px rgba(16, 24, 40, 0.12),
      0 32px 64px -32px rgba(43, 91, 255, 0.18);
    overflow: hidden;
  }

  /* 右侧额度面板 */
  .right-panel {
    flex: 0 0 260px;
    background: #ffffff;
    border-radius: 16px;
    padding: 20px;
    box-shadow:
      0 1px 2px rgba(16, 24, 40, 0.04),
      0 12px 32px -12px rgba(16, 24, 40, 0.12),
      0 32px 64px -32px rgba(43, 91, 255, 0.18);
    height: fit-content;

    .panel-title {
      font-size: 15px;
      font-weight: 600;
      color: #111827;
      margin-bottom: 16px;
      padding-bottom: 10px;
      border-bottom: 1px solid #eef0f5;
    }
    .row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 14px;
      margin-bottom: 12px;
      &:last-child {
        margin-bottom: 0;
      }
      .label {
        color: #6b7280;
      }
      .num {
        font-weight: 600;
        color: #de1c2c;
      }
    }
  }

  /* 卡片头部 */
  .card-head {
    padding: 36px 48px 24px;
    border-bottom: 1px solid #eef0f5;
    background: linear-gradient(180deg, #fbfcff 0%, #ffffff 100%);
    .card-title {
      font-size: 22px;
      font-weight: 600;
      color: #111827;
      letter-spacing: 0.2px;
    }
    .card-desc {
      margin-top: 6px;
      font-size: 14px;
      color: #6b7280;
      b {
        color: #2B5BFF;
        font-weight: 500;
      }
    }
    .steps {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-top: 22px;
      font-size: 13px;
      color: #6b7280;
      .step {
        display: flex;
        align-items: center;
        gap: 8px;
        .step-num {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          display: grid;
          place-items: center;
          background: #eef2ff;
          color: #2B5BFF;
          font-weight: 600;
          font-size: 12px;
        }
        &.active {
          color: #111827;
          font-weight: 500;
          .step-num {
            background: linear-gradient(135deg, #2B5BFF, #6E84FF);
            color: #fff;
            box-shadow: 0 4px 12px -2px rgba(43, 91, 255, 0.4);
          }
        }
      }
      .step-divider {
        width: 28px;
        height: 1px;
        background: #e5e7eb;
      }
    }
  }

  /* 表单主体 */
  .form {
    padding: 32px 48px 40px;
    .section {
      margin-bottom: 32px;
      &:last-child {
        margin-bottom: 0;
      }
      .section-title {
        display: flex;
        align-items: center;
        gap: 10px;
        font-size: 15px;
        font-weight: 600;
        color: #111827;
        margin-bottom: 18px;
        &::before {
          content: "";
          width: 3px;
          height: 14px;
          border-radius: 2px;
          background: linear-gradient(180deg, #2B5BFF, #6E84FF);
        }
        .section-sub {
          font-weight: 400;
          color: #9ca3af;
          font-size: 13px;
        }
      }
    }
    .grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 20px 24px;
    }
    .field {
      display: flex;
      flex-direction: column;
      .label {
        display: flex;
        align-items: center;
        gap: 4px;
        font-size: 13px;
        color: #374151;
        margin-bottom: 8px;
        font-weight: 500;
        .req {
          color: #ef4444;
          font-size: 14px;
          line-height: 1;
        }
        .hint {
          margin-left: 6px;
          font-weight: 400;
          color: #9ca3af;
          font-size: 12px;
        }
      }
      .control {
        position: relative;
        background: #f8fafc;
        border: 1px solid #e5e7eb;
        border-radius: 10px;
        transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
        &:hover {
          border-color: #c7d2fe;
          background: #fbfcff;
        }
        &:focus-within {
          border-color: #2B5BFF;
          background: #ffffff;
          box-shadow: 0 0 0 3px rgba(43, 91, 255, 0.12);
        }
        input {
          width: 100%;
          height: 44px;
          background: transparent;
          border: 0;
          outline: 0;
          padding: 0 14px;
          font-size: 14px;
          color: #111827;
          border-radius: 10px;
          &::placeholder {
            color: #b0b7c3;
          }
        }
      }
      .field-error {
        margin-top: 6px;
        font-size: 12px;
        color: #ef4444;
      }
    }

    /* 上传区域 */
    .upload {
      border: 1.5px dashed #c7d2fe;
      background: linear-gradient(180deg, #fbfcff 0%, #f5f7ff 100%);
      border-radius: 12px;
      padding: 24px 20px;
      display: flex;
      align-items: center;
      gap: 16px;
      transition: border-color 0.15s ease, background 0.15s ease, transform 0.15s ease;
      cursor: pointer;
      &:hover {
        border-color: #2B5BFF;
        background: linear-gradient(180deg, #f5f8ff 0%, #eef3ff 100%);
      }
      .upload-icon {
        width: 44px;
        height: 44px;
        border-radius: 10px;
        background: linear-gradient(135deg, #2B5BFF, #6E84FF);
        color: #fff;
        display: grid;
        place-items: center;
        box-shadow: 0 6px 16px -6px rgba(43, 91, 255, 0.5);
        flex: 0 0 auto;
      }
      .upload-text {
        flex: 1;
        min-width: 0;
        .t1 {
          font-size: 14px;
          color: #111827;
          font-weight: 500;
          .accent {
            color: #2B5BFF;
          }
        }
        .t2 {
          font-size: 12px;
          color: #6b7280;
          margin-top: 2px;
        }
      }
      .upload-btn {
        flex: 0 0 auto;
        height: 36px;
        padding: 0 16px;
        border-radius: 8px;
        background: #ffffff;
        border: 1px solid #e5e7eb;
        color: #374151;
        font-size: 13px;
        font-weight: 500;
        cursor: pointer;
        transition: border-color 0.15s, color 0.15s, background 0.15s;
        &:hover {
          border-color: #2B5BFF;
          color: #2B5BFF;
          background: #f5f8ff;
        }
      }
    }

    /* 协议勾选区域 */
    .agreement {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      padding: 14px 16px;
      background: #f8fafc;
      border: 1px solid #eef0f5;
      border-radius: 10px;
      .check {
        appearance: none;
        -webkit-appearance: none;
        width: 18px;
        height: 18px;
        border-radius: 5px;
        border: 1.5px solid #c7d2fe;
        background: #fff;
        display: inline-grid;
        place-content: center;
        cursor: pointer;
        transition: all 0.15s ease;
        margin-top: 2px;
        flex: 0 0 auto;
        &:hover {
          border-color: #2B5BFF;
        }
        &:checked {
          background: linear-gradient(135deg, #2B5BFF, #6E84FF);
          border-color: transparent;
          &::after {
            content: "";
            width: 10px;
            height: 6px;
            border-left: 2px solid #fff;
            border-bottom: 2px solid #fff;
            transform: rotate(-45deg) translate(1px, -1px);
          }
        }
      }
      .agreement-text {
        font-size: 13px;
        color: #4b5563;
        line-height: 1.6;
        strong {
          color:#1f2937;
        }
        a {
          color: #2B5BFF;
          text-decoration: none;
          font-weight: 500;
          &:hover {
            text-decoration: underline;
          }
        }
      }
    }

    /* 安全提示 */
    .trust {
      margin-top: 16px;
      padding: 12px 16px;
      background: #f5f8ff;
      border-radius: 10px;
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 12px;
      color: #4b5563;
      .trust-dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: #22c55e;
        box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.18);
      }
    }

    /* 按钮区域 */
    .actions {
      margin-top: 28px;
      display: flex;
      align-items: center;
      gap: 12px;
      padding-top: 24px;
      border-top: 1px dashed #eef0f5;
      .btn {
        height: 44px;
        padding: 0 24px;
        border-radius: 10px;
        font-size: 14px;
        font-weight: 500;
        border: 0;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        gap: 8px;
        transition: transform 0.12s ease, box-shadow 0.15s ease, background 0.15s ease, color 0.15s;
        &.btn-primary {
          background: linear-gradient(135deg, #2B5BFF 0%, #4E6BFF 100%);
          color: #fff;
          box-shadow: 0 6px 18px -6px rgba(43, 91, 255, 0.55);
          &:hover {
            box-shadow: 0 10px 24px -6px rgba(43, 91, 255, 0.65);
            transform: translateY(-1px);
          }
          &:active {
            transform: translateY(0);
          }
          &:disabled {
            opacity: 0.7;
            cursor: not-allowed;
            transform: none;
          }
        }
        &.btn-ghost {
          background: #fff;
          color: #4b5563;
          border: 1px solid #e5e7eb;
          &:hover {
            color: #1f2937;
            border-color: #c7d2fe;
            background: #fbfcff;
          }
        }
      }
      .spacer {
        flex: 1;
      }
      .action-tip {
        font-size: 12px;
        color: #9ca3af;
      }
    }
  }

  /* ==========弹窗样式========== */
  .dialog-mask {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.55);
    z-index: 998;
  }
  .dialog-wrap {
    position: fixed;
    inset: 0;
    z-index: 999;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
  }
  .dialog-box {
    width: 100%;
    max-width: 680px;
    background: #fff;
    border-radius: 16px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
  .dialog-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 18px 24px;
    border-bottom: 1px solid #eef0f5;
    h3 {
      margin:0;
      font-size:17px;
      font-weight:600;
    }
    .close-btn {
      width:28px;
      height:28px;
      display:grid;
      place-items:center;
      border-radius:6px;
      cursor:pointer;
      color:#6b7280;
      &:hover{
        background:#f3f4f6;
      }
    }
  }
  .dialog-content {
    padding:20px 24px;
    max-height: 60vh;
    overflow-y:auto;
    font-size:14px;
    line-height:1.7;
    color:#374151;
    p {
      margin: 8px 0;
    }
  }
  .dialog-footer {
    padding:16px 24px;
    border-top:1px solid #eef0f5;
    text-align:right;
  }
  .dialog-btn {
    height:42px;
    padding:0 26px;
    border-radius:9px;
    border:none;
    cursor:pointer;
    font-size:14px;
    &.confirm {
      background: linear-gradient(135deg, #2B5BFF, #6E84FF);
      color:#fff;
    }
  }

  /* 适配移动端 */
  @media (max-width: 720px) {
    padding: 24px 12px;
    flex-direction: column;
    .card-head,
    .form {
      padding-left: 20px;
      padding-right: 20px;
    }
    .grid {
      grid-template-columns: 1fr;
    }
    .steps {
      flex-wrap: wrap;
    }
    .right-panel {
      width: 100%;
      flex: none;
    }
    .dialog-box {
      max-width: 100%;
    }
  }
}
</style>